import { create } from "zustand";

// ========================================
// TOAST TYPES
// ========================================
export interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info" | "warning";
  duration?: number;
}

// ========================================
// UI STORE STATE
// ========================================
interface UIState {
  // Toasts
  toasts: Toast[];
  addToast: (toast: Omit<Toast, "id">) => void;
  removeToast: (id: string) => void;

  // Global animations
  animationsEnabled: boolean;
  toggleAnimations: () => void;
}

// ========================================
// UI STORE
// ========================================
export const useUIStore = create<UIState>((set) => ({
  // ========================================
  // TOASTS
  // ========================================
  toasts: [],

  addToast: (toast) => {
    const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));

    // Auto-remove after duration (default 4s)
    const duration = toast.duration || 4000;
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, duration);
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  // ========================================
  // ANIMATIONS
  // ========================================
  animationsEnabled: true,

  toggleAnimations: () =>
    set((state) => ({ animationsEnabled: !state.animationsEnabled })),
}));

// ========================================
// HELPER HOOKS
// ========================================

// Simplified toast hook
export const useToast = () => {
  const { addToast } = useUIStore();

  return {
    success: (message: string, duration?: number) =>
      addToast({ message, type: "success", duration }),
    error: (message: string, duration?: number) =>
      addToast({ message, type: "error", duration }),
    info: (message: string, duration?: number) =>
      addToast({ message, type: "info", duration }),
    warning: (message: string, duration?: number) =>
      addToast({ message, type: "warning", duration }),
  };
};
